import angular, { IPromise } from 'angular'
import filter from 'lodash/filter'
import find from 'lodash/find'
import map from 'lodash/map'
import sortBy from 'lodash/sortBy'
import moment from 'moment-timezone'
import {
  ApiCustomer,
  ApiCustomerGroup,
  ApiOutlet,
  ApiPriceBook,
  ApiPriceBookProduct,
} from '@vend/types'

import { customerGroupsStore } from '../factories/database/customer-groups-store'
import { priceBookProductsStore } from '../factories/database/price-book-products-store'
import { priceBooksStore } from '../factories/database/price-books-store'
import { isGroupAllCustomersGroup } from '../functions/customer'
import { filterPriceBookProducts } from '../functions/price-book'
import { LineItem } from '../models/line-item'
import { $log } from '../ng-compat/imports'
import { PRICE_BOOK_TYPE_BASE } from '../sale-calculation/calculate-line-item'
import { registerManager, syncManager } from '../ng-compat/managers'
import { posStateManager } from './pos-state-manager'

const PLATFORMS = {
  ALL_PLATFORMS: '0',
  IN_STORE: '1',
  ECOMMERCE: '2',
}

const ALLOWED_PLATFORMS = [PLATFORMS.ALL_PLATFORMS, PLATFORMS.IN_STORE]

export type PriceBookInfo = {
  priceBook?: ApiPriceBook
  priceBookProduct: ApiPriceBookProduct
  customerGroup?: ApiCustomerGroup
}

/**
 * Manages price books and price book products.
 *
 * @class PriceBookManager
 */
export class PriceBookManager {
  private _basePriceBook: ApiPriceBook | null | undefined
  private _whenInitialised: IPromise<void> | undefined
  private posStateManager = posStateManager
  private registerManager = registerManager
  private syncManager = syncManager

  constructor() {
    this._basePriceBook = null
  }

  // ============================== Public =============================== //
  /**
   * Initialise the manager.
   *
   * @method init
   *
   * @return {Promise} A promise resolved once initialisation is complete
   */
  public init() {
    if (!this._whenInitialised) {
      this._whenInitialised = this.syncManager
        .whenCompleteEntityDataAvailable('priceBooks')
        .then(() => {
          priceBooksStore.on('sync', this._determineBasePriceBook, this)
          return this._determineBasePriceBook()
        })
    }
    return this._whenInitialised
  }

  /**
   * The base price book.
   *
   * @property basePriceBook
   * @type {PriceBook}
   */
  public get basePriceBook() {
    return this._basePriceBook
  }

  /**
   * Get the applicable price book information (price books and price book products) for the given LineItem.
   *
   * @method getPriceBookInfoForLineItem
   *
   * @param  {LineItem[]} lineItems
   *         The line items of the sale
   *
   * @param  {Customer} customer
   *         The customer of the sale
   *
   * @param  {LineItem} lineItem
   *         The line item to get the price information for
   *
   * @return {Promise<PriceBookInfo[]>} The array of price book information (priceBook, priceBookProduct) for the given line item
   */
  public getPriceBookInfoForLineItem(
    lineItems: LineItem[],
    customer: ApiCustomer | null,
    lineItem: LineItem
  ): IPromise<PriceBookInfo[]> {
    return priceBookProductsStore
      .getByProductId(lineItem.product.id)
      .then(priceBookProducts =>
        filterPriceBookProducts(lineItems, lineItem, priceBookProducts)
      )
      .then(priceBookProducts =>
        this._resolvePriceBooksAndCustomerGroups(priceBookProducts)
      )
      .then(priceBookInfos => {
        return this._filterPriceBookInfo(customer, priceBookInfos)
      })
  }

  /**
   * Get the base price book product entry for the given product ID.
   *
   * @method getBasePriceBookProduct
   *
   * @param  {String} productId
   *         The ID of the product to get the base price book product for
   *
   * @return {Promise<ApiPriceBookProduct>} A promise resolved with the PriceBookProduct associated with the given
   *         product ID and the base PriceBook
   */
  public getBasePriceBookProduct(
    productId: string
  ): IPromise<ApiPriceBookProduct | undefined> {
    return priceBookProductsStore
      .getByProductId(productId)
      .then(priceBookProducts => {
        let basePriceBookProduct
        if (this.basePriceBook) {
          basePriceBookProduct = find(priceBookProducts, {
            price_book_id: this.basePriceBook.id,
          })
        }
        return basePriceBookProduct
      })
  }

  // ============================== Private =============================== //
  /**
   * Find and cache the base pricebook.
   *
   * @private
   * @method _determineBasePriceBook
   *
   * @return {Promise} A promise resolved once the base pricebook has been found and cached
   */
  private _determineBasePriceBook() {
    return priceBooksStore.all().then(priceBooks => {
      const sortedPriceBooks = sortBy(priceBooks, 'id')
      this._basePriceBook = find(sortedPriceBooks, {
        type: PRICE_BOOK_TYPE_BASE,
      })
    })
  }

  /**
   * Resolves the price books and customer groups associated with the eligible price book products passed.
   *
   * @private
   * @method _resolvePriceBooksAndCustomerGroups
   *
   * @param  priceBookProducts {Array}
   *         The eligible price book products related to the line item product id.
   *
   * @return {Promise} A promise resolved with the array of eligible price books, price book products and
   *         customer groups.
   */
  private _resolvePriceBooksAndCustomerGroups(
    priceBookProducts: ApiPriceBookProduct[]
  ): IPromise<PriceBookInfo[]> {
    return Promise.all(
      map(priceBookProducts, priceBookProduct => {
        return priceBooksStore
          .get(priceBookProduct.price_book_id!)
          .then(priceBook => {
            if (priceBook?.customer_group_id) {
              return customerGroupsStore
                .get(priceBook.customer_group_id)
                .then(customerGroup => ({
                  priceBook,
                  priceBookProduct,
                  customerGroup,
                }))
            }
            return { priceBook, priceBookProduct }
          })
      })
    )
  }

  /**
   * Filter the collection of price book products and their associated price books to only include those price books
   * that are applicable to the current platform, outlet, customer group, and date range.
   *
   * @private
   * @method _filterPriceBookInfo
   *
   * @param  {Sale} sale
   *         The sale related to the price being determined (for evaluating the customer group)
   *
   * @param  {Array} priceBookInfos
   *         Array of price books and price book products
   *
   * @return {Array} The filtered collection of price book info
   */
  private _filterPriceBookInfo(
    customer: ApiCustomer | null,
    priceBookInfos: $TSFixMe[]
  ) {
    return filter(
      priceBookInfos,
      ({ priceBook, priceBookProduct, customerGroup }) => {
        if (!priceBook) {
          $log.warn(
            '[PriceBook Manager] No pricebook associated with this pricebook product',
            priceBookProduct
          )
          return false
        }
        return this.passesPriceBookConditions(
          customer,
          priceBook,
          customerGroup
        )
      }
    )
  }

  /**
   * Returns true if given sale passes given price book's outlet, customer & date conditions.
   *
   * @public
   * @method passesPriceBookConditions
   * @public
   * @method passesPriceBookConditions
   *
   * @param  sale {Sale}
   *         The Sale to check conditions against.
   *
   * @param  priceBook {PriceBook}
   *         The price book with certain condition settings.
   *
   * @param  customerGroup {CustomerGroup}
   *         The customer group the price book is restricted to
   *
   * @return {Boolean} True if conditions all pass, otherwise false.
   */
  public passesPriceBookConditions(
    customer: ApiCustomer | null,
    priceBook: ApiPriceBook,
    customerGroup?: ApiCustomerGroup
  ) {
    if (!this._isPriceBookAvailableInStore(priceBook)) {
      // Not available "In Store" (e.g. restricted to online only)
      return false
    }

    // 2.0 support
    if (
      priceBook.outlet_id !== undefined &&
      !this._isCurrentOutlet(priceBook.outlet_id)
    ) {
      // Not eligible due to outlet.
      return false
    }
    // 2.0 support
    if (
      priceBook.customer_group_id !== undefined &&
      !this._isCustomerInApplicableGroup(customer, customerGroup)
    ) {
      // Not eligible due to customer.
      return false
    }

    // 3.0 support
    if (
      priceBook.outlets !== undefined &&
      !this._isOutletsInCurrentOutlet(priceBook.outlets)
    ) {
      // Not eligible due to outlet.
      return false
    }
    // 3.0 support
    if (
      priceBook.customer_groups !== undefined &&
      !this._isCustomersInApplicableGroup(customer, priceBook.customer_groups)
    ) {
      // Not eligible due to customer.
      return false
    }

    if (
      !this._isCurrentDateWithinDateRange(
        priceBook.valid_from,
        priceBook.valid_to
      )
    ) {
      // Not eligible due to date.
      return false
    }
    return true
  }

  /**
   * Returns true if the given price book is available for in store customers.
   *
   * @private
   * @method _isPriceBookAvailableInStore
   *
   * @param  {PriceBook} priceBook
   *         The price book to check
   *
   * @return {Boolean} true if the price book is available In Store, false if it is e.g. restricted to online only
   */
  private _isPriceBookAvailableInStore(priceBook: ApiPriceBook) {
    return (
      ALLOWED_PLATFORMS.indexOf(String(priceBook.restrict_to_platform_key)) >= 0
    )
  }

  /**
   * Returns true if the given outlet id is either null (no restriction) or is that of the current outlet.
   *
   * @private
   * @method _isCurrentOutlet
   *
   * @param  outletId {String}
   *         The id of the outlet to check if is current.
   *
   * @return {Boolean} True if the given id belongs to the current outlet.
   */
  private _isCurrentOutlet(outletId: string | null | undefined) {
    if (outletId === null || outletId === this.posStateManager.getOutletId()) {
      return true
    }
    return false
  }

  /**
   * Returns true if the given array of outlets is either null (no restriction) or has the current outlet.
   *
   * @private
   * @method _isOutletInCurrentOutlet
   *
   * @param  outlets {Outlet[]}
   *         The array of outlets to check.
   *
   * @return {Boolean} True if one of the given outlets belong to the current outlet.
   */
  private _isOutletsInCurrentOutlet(outlets: ApiOutlet[] | null) {
    if (
      outlets === null ||
      outlets.some(outlet => outlet.id === this.posStateManager.getOutletId())
    ) {
      return true
    }
    return false
  }

  /**
   * Returns true if the given customer applies to the given customer group. The rules for applicability are:
   *
   * 1) The "All Customers" group by definition applies to all customers. This is also the case for a non-existant
   * (null/undefined) group.
   *
   * 2) A non-existant customer (null/undefined) implies the WALKIN customer which is always a member of (and
   * hence applies to) the "All Customers" group and never any other group.
   *
   * 3) Finally a non-WALKIN customer applies to an explicit (non-"All Customers") group if the customer's
   * `customer_group_id` property matches the group's `id` property.
   *
   * @private
   * @method _isCustomerInApplicableGroup
   *
   * @param  customer {Customer}
   *         The customer associated with the current sale
   *
   * @param  customerGroup {CustomerGroup}
   *         The customer group the price book is restricted to
   *
   * @return {Boolean} True if the given customer is in the given group
   */
  private _isCustomerInApplicableGroup(
    customer: ApiCustomer | null,
    customerGroup?: ApiCustomerGroup
  ) {
    if (!customerGroup || isGroupAllCustomersGroup(customerGroup)) {
      // Price book is not restricted to an explicit customer group
      return true
    } else if (customer && customer.customer_group_id === customerGroup.id) {
      // Sale has a valid customer that is in the applicable customer group
      return true
    } else {
      // Sale either has no customer or that customer is not in the applicable customer group
      return false
    }
  }

  /**
   * Returns true if one of the given customers applies to the given customer group. The rules for applicability are:
   *
   * 1) The "All Customers" group by definition applies to all customers. This is also the case for a non-existant
   * (null/undefined) group.
   *
   * 2) A non-existant customer (null/undefined) implies the WALKIN customer which is always a member of (and
   * hence applies to) the "All Customers" group and never any other group.
   *
   * 3) Finally a non-WALKIN customer applies to an explicit (non-"All Customers") group if the customer's
   * `customer_group_id` property matches one of the group's `id` property.
   *
   * @private
   * @method _isCustomersInApplicableGroup
   *
   * @param  customer {Customer}
   *         The customer associated with the current sale.
   *
   * @param  customerGroups {CustomerGroups}
   *         The customer groups that the price book is restricted to.
   *
   * @return {Boolean} True if one of the given customers is in the given group.
   */
  private _isCustomersInApplicableGroup(
    customer: ApiCustomer | null,
    customerGroups: ApiCustomerGroup[]
  ) {
    if (
      customerGroups.some(customerGroup =>
        isGroupAllCustomersGroup(customerGroup)
      )
    ) {
      // Price book is not restricted to an explicit customer group
      return true
    } else if (
      customer &&
      customerGroups.some(
        customerGroup => customerGroup.id === customer.customer_group_id
      )
    ) {
      // Sale has a valid customer that is in the applicable customer group
      return true
    } else {
      // Sale either has no customer or that customer is not in the applicable customer group
      return false
    }
  }

  /**
   * Returns true if the current date (in the active outlet's timezone) is within the given date range (inclusive)
   * or the given date range is not set (null).
   *
   * @private
   * @method _isCurrentDateWithinDateRange
   *
   * @param  fromDate {Date|null}
   *         The from / start date of the date range that the current date must be within.
   *
   * @param  toDate {Date|null}
   *         The to / end date of the date range that the current date must be within.
   *
   * @return {Boolean} True if the current date is within the given date range, otherwise false.
   */
  private _isCurrentDateWithinDateRange(
    fromDate: Date | string | null,
    toDate: Date | string | null
  ) {
    const currentDate = this._getDateInActiveOutletTimezone()
    const beforeFromDate = currentDate.isBefore(fromDate, 'day')
    // Pricebooks run up to (but not including) the defined `toDate`
    const sameOrAfterToDate = currentDate.isSameOrAfter(toDate, 'day')
    if (beforeFromDate || sameOrAfterToDate) {
      return false
    } else {
      return true
    }
  }

  /**
   * Get the current date in the active outlet's timezone wrapped in a Moment.
   *
   * @private
   * @method _getDateInActiveOutletTimezone
   *
   * @return {Moment} Moment representing the current date in the active outlet's timezone
   */
  private _getDateInActiveOutletTimezone() {
    const activeOutletTimezone = this.registerManager.getActiveOutlet()
      .time_zone
    const currentDateInTimezone = moment().tz(activeOutletTimezone)
    // Format to just the date and re-wrap in a Moment
    return moment(currentDateInTimezone.format('YYYY-MM-DD'))
  }
}

let priceBookManager: PriceBookManager

export const getPriceBookManager = () => {
  if (!priceBookManager) {
    priceBookManager = new PriceBookManager()
  }
  return priceBookManager
}

/**
 * Manager for price book related operations.
 *
 * @module priceBookManager
 */
angular
  .module('webregister.managers')
  .factory('priceBookManager', () => getPriceBookManager())
